import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Plus, ShieldCheck, Trash2, Users } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { CatalogoDePermisos, Rol } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { PanelLateral, CampoDelPanel, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { SelectorDePermisos } from './SelectorDePermisos';
import { insigniaDeRol, tituloDeRol } from './rolEnPantalla';

const ROL_ADMINISTRADOR = 'admin';

const esquema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio').max(50),
  descripcion: z.string().max(255).optional(),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaRoles() {
  const clienteDeConsultas = useQueryClient();

  const [buscar, setBuscar] = useState('');
  const [editando, setEditando] = useState<Rol | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<Rol | null>(null);
  const [error, setError] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Rol[]>('/roles') });
  const catalogo = useQuery({
    queryKey: ['permisos'],
    queryFn: () => api.get<CatalogoDePermisos>('/permisos'),

    staleTime: Infinity,
  });

  const totalDePermisos = useMemo(
    () => (catalogo.data ? Object.values(catalogo.data).flat().length : 0),
    [catalogo.data],
  );

  const filtrados = useMemo(() => {
    const texto = buscar.trim().toLowerCase();
    const lista = roles.data ?? [];
    if (!texto) {
      return lista;
    }
    return lista.filter(
      (r) =>
        r.nombre.toLowerCase().includes(texto) ||
        (r.descripcion ?? '').toLowerCase().includes(texto),
    );
  }, [roles.data, buscar]);

  const pagina = {
    contenido: filtrados,
    pagina: 0,
    tamanio: filtrados.length || 1,
    totalElementos: filtrados.length,
    totalPaginas: 1,
  };

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/roles/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      setError(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['roles'] });
      notificar.exito('Rol eliminado');
    },
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo eliminar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const columnas: Columna<Rol>[] = [
    {
      clave: 'n',
      encabezado: '#',
      clase: 'w-12 pl-5 pr-2',
      contenido: (_, i) => <span className="importe text-armazon-400">{i + 1}</span>,
    },
    {
      clave: 'rol',
      encabezado: 'Rol',
      contenido: (r) => {
        const { icono: Icono, color } = insigniaDeRol(r.nombre);
        return (
          <div className="flex items-center gap-2.5">
            <span className={`grid size-8 shrink-0 place-items-center rounded-lg ${color}`}>
              <Icono className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-armazon-800">{tituloDeRol(r.nombre)}</p>
              {/*
                Debajo, el identificador que de verdad guarda la base. Quien administra
                permisos lo necesita: es el que viaja en la API y el que se escribe en los
                parametros, como `puntos_rol_responsable`.
              */}
              <p className="text-[10px] font-medium uppercase text-armazon-400">
                {r.nombre}
                {r.esDelSistema && ' · Rol del sistema'}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      clave: 'descripcion',
      encabezado: 'Descripción',
      contenido: (r) =>
        r.descripcion ? (
          <span className="text-xs uppercase text-armazon-600">{r.descripcion}</span>
        ) : (
          <span className="text-xs text-armazon-300">—</span>
        ),
    },
    {
      clave: 'permisos',
      encabezado: 'Permisos',
      alinear: 'centro',
      clase: 'w-28',
      contenido: (r) =>

        r.nombre === ROL_ADMINISTRADOR ? (
          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
            Todos
          </span>
        ) : (
          <span className="importe rounded-full bg-armazon-100 px-2.5 py-1 text-xs font-semibold text-armazon-700">
            {r.permisos.length}
          </span>
        ),
    },
    {
      clave: 'usuarios',
      encabezado: 'Usuarios',
      alinear: 'centro',
      clase: 'w-24',
      contenido: (r) => (
        <span className="inline-flex items-center gap-1.5 text-xs text-armazon-500">
          <Users className="size-3 text-indigo-500" aria-hidden />
          <span className="importe font-semibold text-armazon-700">{r.usuarios}</span>
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-24 pl-2 pr-5',
      contenido: (r) => {
        const esAdmin = r.nombre === ROL_ADMINISTRADOR;

        if (esAdmin) {
          return <span className="text-xs uppercase text-armazon-300">Solo lectura</span>;
        }

        return (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => setEditando(r)}
              title="Editar"
              aria-label={`Editar el rol ${r.nombre}`}
              className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
            >
              <Pencil className="size-[15px]" aria-hidden />
            </button>

            {!r.esDelSistema && (
              <button
                type="button"
                onClick={() => setAEliminar(r)}
                title="Eliminar"
                aria-label={`Eliminar el rol ${r.nombre}`}
                className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
              >
                <Trash2 className="size-[15px]" aria-hidden />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={ShieldCheck}
        color="violeta"
        titulo="Roles y permisos"
        subtitulo="Qué puede hacer cada perfil dentro del sistema"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo rol"
            aria-label="Nuevo rol"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <Tabla
        datos={pagina}
        cargando={roles.isPending}
        columnas={columnas}
        tamanio={filtrados.length || 10}
        alCambiarPagina={() => {}}
        alCambiarTamanio={() => {}}
        iconoVacio={ShieldCheck}
        mensajeVacio="Ningún rol coincide con la búsqueda"
        entidad="roles"
        herramientas={
          <CampoDeBusqueda
            valor={buscar}
            alCambiar={setBuscar}
            marcador="Buscar por nombre o descripción…"
            className="w-full max-w-xs"
          />
        }
      />

      {catalogo.data && (
        <FormularioDeRol
          key={editando?.id ?? 'nuevo'}
          abierto={creando || editando !== null}
          rol={editando}
          catalogo={catalogo.data}
          totalDePermisos={totalDePermisos}
          alCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
          alGuardar={() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['roles'] });

            void clienteDeConsultas.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      )}

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar rol"
        descripcion="Se eliminará el perfil y sus permisos asignados:"
        detalle={aEliminar ? aEliminar.nombre : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function FormularioDeRol({
  abierto,
  rol,
  catalogo,
  totalDePermisos,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  rol: Rol | null;
  catalogo: CatalogoDePermisos;
  totalDePermisos: number;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = rol !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<number>>(() => marcadosDe(rol, catalogo));

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      nombre: rol?.nombre ?? '',
      descripcion: rol?.descripcion ?? '',
    },
  });

  function alternar(id: number) {
    setMarcados((previos) => {
      const siguiente = new Set(previos);
      if (siguiente.has(id)) {
        siguiente.delete(id);
      } else {
        siguiente.add(id);
      }
      return siguiente;
    });
  }

  function alternarModulo(ids: number[], marcarTodos: boolean) {
    setMarcados((previos) => {
      const siguiente = new Set(previos);
      for (const id of ids) {
        if (marcarTodos) {
          siguiente.add(id);
        } else {
          siguiente.delete(id);
        }
      }
      return siguiente;
    });
  }

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const cuerpo = {
        nombre: datos.nombre,
        descripcion: datos.descripcion || null,
        permisoIds: [...marcados],
      };

      if (editando) {
        await api.put<Rol>(`/roles/${rol.id}`, cuerpo);
      } else {
        await api.post<Rol>('/roles', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Rol ${editando ? 'actualizado' : 'guardado'}`);
    } catch (causa) {
      setErrorGeneral(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el rol');
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el rol',
      );
    }
  });

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar rol' : 'Nuevo rol'}
      descripcion={editando ? `${marcados.size} de ${totalDePermisos} permisos` : 'Ingrese los datos del nuevo rol'}
      icono={ShieldCheck}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Nombre"
        requerido
        error={errors.nombre?.message}
        ayuda={
          rol?.esDelSistema
            ? 'Es un rol del sistema: el nombre no se puede cambiar'
            : 'El nombre «admin» está reservado para el rol del sistema'
        }
      >
        <input
          {...register('nombre')}
          className={claseEntradaPanel}
          autoFocus
          readOnly={rol?.esDelSistema ?? false}
        />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Descripción" error={errors.descripcion?.message}>
        <input
          placeholder="Ventanilla de recaudación"
          {...register('descripcion')}
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Permisos" requerido>
        <SelectorDePermisos
          catalogo={catalogo}
          marcados={marcados}
          alAlternar={alternar}
          alAlternarModulo={alternarModulo}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}

function marcadosDe(rol: Rol | null, catalogo: CatalogoDePermisos): Set<number> {
  if (!rol) {
    return new Set();
  }
  const porSlug = new Map(Object.values(catalogo).flat().map((p) => [p.slug, p.id] as const));
  return new Set(
    rol.permisos.map((slug) => porSlug.get(slug)).filter((id): id is number => id !== undefined),
  );
}
